import { isToday, isValid, parse } from 'date-fns';
import channelsUtil from './channelsUtil';
import h from './common';
import { checkShowLockIcon } from './vod';
import { IChannels } from '../features/epg/types/iEpgGudeContainer';

type Pluck<T, K extends keyof T> = (items: T[], key: K) => T[K][];

const pluck: Pluck<any, string> = (items, key) => {
    return items?.length && items?.map((item) => item?.[key]);
};

const formatter = {
    arrayChunk: (array: any[], chunk = 10): any[][] => {
        const tempArray: any[][] = [];
        for (let i = 0, j = array.length; i < j; i += chunk) {
            tempArray.push(array.slice(i, i + chunk));
        }
        return tempArray;
    },
    convertSecondsToMinutes: (seconds = 0): number => {
        return Math.floor(seconds / 60);
    },
    /**
     * Replace placeholders in text with corresponding values from placeholders object
     * @param text - The text containing placeholders
     * @param placeholders - An object containing placeholder values
     * @param placeholderExpression - Regular expression to match placeholders
     * @returns The text with replaced values
     */
    getPlaceholderValue: (
        text: string,
        placeholders?: { [key: string]: string },
        placeholderExpression = /<[a-zA-Z]*>/g // Added A-Z to support capital letters
    ): string => {
        if (placeholderExpression.test(text)) {
            const values = text.match(placeholderExpression);
            if (values) {
                for (let i = 0; i < values.length; i++) {
                    const valueKey = values[i].replace(/[^\w]/gi, '');
                    if (valueKey in placeholders) {
                        text = text.replace(values[i], placeholders[valueKey]);
                    }
                }
            }
        }
        return text;
    },
    getStripScriptContents: (htmlContent: string): string => {
        if (htmlContent) {
            return htmlContent.replace(/<script(.*?)>(.*?)<\/script>/is, '');
        }
        return htmlContent;
    },
    convertHourstoMilliSecond: (hours: number): number => {
        return Math.floor(hours * 60 * 60 * 1000);
    },
    converToHHMMSS: (secs: number): string => {
        const sec_num = parseInt(secs.toString(), 10);
        const hours = Math.floor(sec_num / 3600);
        const minutes = Math.floor(sec_num / 60) % 60;
        const seconds = sec_num % 60;

        return [hours, minutes, seconds]
            .map((v) => (v < 10 ? '0' + v : v))
            .filter((v, i) => v !== '00' || i > 0)
            .join(':');
    },
    formatEpisodeNumber: (num: number): string => {
        if (num < 10) {
            return '0' + num.toString();
        }
        return num.toString();
    },
    isArabicText: (val: string): boolean => new RegExp(`^(?:[\u0660-\u0669\u0621-\u064A0-9 ]+)$`, 'i').test(val),
    pluck: pluck,
    getTimeRange: (catchupBuffer: number) => {
        const nowTimeStamp = Date.now();
        const catchupBuffer1 = catchupBuffer * 60 * 60 * 1000; //will comment later when catchupBuffer is available
        const startTimeStamp = nowTimeStamp - catchupBuffer1;
        return `${startTimeStamp}~${nowTimeStamp}`;
    },
    /**
     * Formats an array of channel objects into a standardized structure used in the app.
     * Handles multilingual titles, logo URL construction using a given image ID, lock status, and channel metadata.
     *
     * @param {Array} channelsToBeFormatted - The raw list of channel data.
     * @param {string} channelImageId - Preferred image ID to use for each channel's logo.
     * @returns {Array} Formatted list of channel objects with metadata.
     */
    formattedChannelData: (channelsToBeFormatted = [], channelImageId: string): Partial<IChannels>[] => {
        const filteredChannels = channelsUtil?.getFilterChannelBySubGuid(channelsToBeFormatted);
        return (
            filteredChannels.map((channel, idx) => {
                // Attempt to find the preferred image by ID or fallback to the first image
                const channelImageUrl =
                    channel.channelImages?.find((img) => img.id === channelImageId)?.url ||
                    channel.channelImages?.[0]?.url ||
                    '';

                // Extract subscription GUIDs to determine lock status
                const subscriptionGuids = pluck(channel.subscriptions, 'subscriptionGUID');
                const isLocked = checkShowLockIcon(subscriptionGuids ?? [], channel);

                // Destructure channel fields with sensible defaults
                const {
                    channelID = null,
                    channelTitle = '',
                    channelTitleAr = '',
                    tuningURLs = null,
                    channelNumber = 'NA',
                    rights = null,
                } = channel;

                return {
                    id: channelID,
                    uuid: channelID, // Keeping uuid same as id for now
                    logo: channelImageId.toLowerCase().includes('svg')
                        ? channelImageUrl
                        : h.getImagizerUrl(channelImageUrl, 176), // Generate optimized image URL
                    title: h.getLang() === 'en' ? channelTitle : channelTitleAr, // Use title based on language
                    locked: isLocked,
                    type: 'channel',
                    index: idx,
                    tuningURLs,
                    channelNumber,
                    rights,
                    category: channel?.category || [],
                    subscriptions: channel?.subscriptions || [],
                };
            }) || []
        );
    },
    /**
     * Transforms an array of date strings into an array of objects containing:
     * - the original date (`name`)
     * - a human-readable formatted label (`label`) Example: "Monday Apr 22"
     *
     * @param {string[]} dates - Array of date strings to format.
     * @returns {Array<{name: string, label: string}>} Array of formatted date objects.
     */
    getReadableFormattedDates: (dates: string[]) => {
        return dates.map((date) => ({
            name: date,
            label: channelsUtil?.getFormattedTime(date, 'dddd MMM D'),
            isToday: formatter?.isTodayChecker(date),
        }));
    },
    /**
     * Checks whether the given date string represents today's date.
     *
     * @param date - The date string to check (e.g., '2025-04-28').
     * @param format - The expected format of the input date string, default is 'yyyy-MM-dd'.
     * @returns `true` if the date is today, otherwise `false`.
     */
    isTodayChecker: (date: string, format = 'yyyy-MM-dd'): boolean => {
        // Parse the input string into a Date object using the provided format
        const givenDate = parse(date, format, new Date());

        // Validate the parsed date
        if (!isValid(givenDate)) {
            return false;
        }

        // Return true if the parsed date is today
        return isToday(givenDate);
    },
};

export default formatter;
